/**
 * Word-level diff, for showing an author exactly what an AI revision would change
 * before it touches their memo.
 *
 * Diffs the *text*, not the HTML. A revision that rewraps a paragraph changes
 * tags in ways nobody cares about, and a diff full of `<p>` noise is a diff
 * nobody reads — which would make the confirmation step worthless.
 *
 * Written rather than pulled from a library because the whole implementation is
 * an LCS and the alternative is another dependency in the bundle for something
 * this small.
 */

export type DiffKind = "same" | "added" | "removed";

export interface DiffSpan {
  kind: DiffKind;
  text: string;
}

/**
 * Above this, the quadratic table stops being free.
 *
 * Reached only by a revision of a very long selection, where a word-level diff
 * was never going to be readable anyway — the caller falls back to showing the
 * two versions side by side.
 */
export const MAX_DIFF_TOKENS = 2500;

/** Words and the whitespace between them, kept so the diff can be re-rendered. */
export const tokenize = (text: string): string[] =>
  (text ?? "").match(/\s+|[^\s]+/g) ?? [];

export interface DiffResult {
  spans: DiffSpan[];
  /** False when the passage was too long to diff word by word. */
  wordLevel: boolean;
  changed: boolean;
}

export const diffWords = (before: string, after: string): DiffResult => {
  const beforeTokens = tokenize(before);
  const afterTokens = tokenize(after);

  if (before === after) {
    return {
      spans: before ? [{ kind: "same", text: before }] : [],
      wordLevel: true,
      changed: false,
    };
  }

  /*
   * Trim the matching ends first.
   *
   * Not just an optimisation: a typical revision changes one clause in the
   * middle, so this reduces the table from the whole passage to the part that
   * actually differs, and keeps the common case well under the cap.
   */
  let start = 0;
  while (
    start < beforeTokens.length &&
    start < afterTokens.length &&
    beforeTokens[start] === afterTokens[start]
  ) {
    start += 1;
  }

  let end = 0;
  while (
    end < beforeTokens.length - start &&
    end < afterTokens.length - start &&
    beforeTokens[beforeTokens.length - 1 - end] ===
      afterTokens[afterTokens.length - 1 - end]
  ) {
    end += 1;
  }

  const beforeMiddle = beforeTokens.slice(start, beforeTokens.length - end);
  const afterMiddle = afterTokens.slice(start, afterTokens.length - end);

  const prefix = beforeTokens.slice(0, start).join("");
  const suffix = beforeTokens.slice(beforeTokens.length - end).join("");

  if (
    beforeMiddle.length > MAX_DIFF_TOKENS ||
    afterMiddle.length > MAX_DIFF_TOKENS
  ) {
    return {
      spans: [
        { kind: "removed", text: before },
        { kind: "added", text: after },
      ],
      wordLevel: false,
      changed: true,
    };
  }

  const middle = lcsSpans(beforeMiddle, afterMiddle);

  const spans: DiffSpan[] = [];
  if (prefix) spans.push({ kind: "same", text: prefix });
  spans.push(...middle);
  if (suffix) spans.push({ kind: "same", text: suffix });

  return { spans: merge(spans), wordLevel: true, changed: true };
};

/** Longest common subsequence over tokens, walked back into spans. */
const lcsSpans = (before: string[], after: string[]): DiffSpan[] => {
  const rows = before.length;
  const columns = after.length;

  // table[i][j] = LCS length of before[i:] and after[j:]
  const table: number[][] = Array.from({ length: rows + 1 }, () =>
    new Array<number>(columns + 1).fill(0)
  );

  for (let i = rows - 1; i >= 0; i -= 1) {
    for (let j = columns - 1; j >= 0; j -= 1) {
      table[i][j] =
        before[i] === after[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const spans: DiffSpan[] = [];
  let i = 0;
  let j = 0;

  while (i < rows && j < columns) {
    if (before[i] === after[j]) {
      spans.push({ kind: "same", text: before[i] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      spans.push({ kind: "removed", text: before[i] });
      i += 1;
    } else {
      spans.push({ kind: "added", text: after[j] });
      j += 1;
    }
  }

  while (i < rows) {
    spans.push({ kind: "removed", text: before[i] });
    i += 1;
  }
  while (j < columns) {
    spans.push({ kind: "added", text: after[j] });
    j += 1;
  }

  return spans;
};

/**
 * Join neighbouring spans of the same kind.
 *
 * Per-token spans would render as one element per word, which flickers on
 * re-render and makes a changed phrase look like a row of separate edits.
 */
const merge = (spans: DiffSpan[]): DiffSpan[] => {
  const merged: DiffSpan[] = [];

  for (const span of spans) {
    if (!span.text) continue;
    const last = merged[merged.length - 1];
    if (last && last.kind === span.kind) {
      last.text += span.text;
      continue;
    }
    merged.push({ ...span });
  }

  return merged;
};

/** A short summary for the confirmation button: "12 added, 4 removed". */
export const describeDiff = (spans: DiffSpan[]): string => {
  const count = (kind: DiffKind) =>
    spans
      .filter((span) => span.kind === kind)
      .reduce((total, span) => total + tokenize(span.text).filter((token) => token.trim()).length, 0);

  const added = count("added");
  const removed = count("removed");

  if (!added && !removed) return "No changes";

  const parts: string[] = [];
  if (added) parts.push(`${added} added`);
  if (removed) parts.push(`${removed} removed`);
  return parts.join(", ");
};
